import React, { useState, useEffect } from 'react';

import InputField from '@components/form/input';
import BoxHeader from '@components/layout/box-header';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import {
  arrayMove,
  SortableContainer,
  SortEnd,
  SortableElement,
} from 'react-sortable-hoc';
import styled from 'styled-components';
import LinksDropdown from './links-dropdown';
import SortableHandleButton from '@components/basics/sortable-button';
import { parseLink } from '@webapp/sdk/tracpage';
import { isValidUrl } from '@webapp/utils/validators';
import { UpdateTracPageField } from '@webapp/store/tracpage/action-creators';
import { TracPage } from '@services/tracpage/types/trac-page';
import Menu from '@components/menu';

import { CloseMenu } from '@webapp/store/ui/action-creators';
import { Typography } from '@components/basics/typography';
declare type LinkWithTitle = TracPageLink & { showTitle?: boolean };
declare type LinkWithOldVer = TracPageLink & { oldLink?: TracPageLink };

interface LinkCtrlProps {
  className?: string;
  links: TracPageLink[];
  closeMenu: () => CloseMenu;
  updateLinks: (
    value: TracPageLink[]
  ) => UpdateTracPageField<TracPage, 'links'>;
}

const mapStateToProps = (state: AppState) => ({
  links: state.tracpage.artistPage.links || [],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  updateLinks: (links: TracPageLink[]) =>
    dispatch(
      new UpdateTracPageField<TracPage, 'links'>('tracpage', 'links', links)
    ),
});

const SortableLinkItem = SortableElement(
  ({
    link,
    withHandle,
    handleChange,
    handleRemove,
    isLoading,
    setOnKeyEnter,
  }: {
    withHandle: boolean;
    link: LinkWithTitle;
    isLoading: boolean;
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageLink>(
      order: number,
      field: T,
      value: TracPageLink[T]
    ) => void;
    setOnKeyEnter: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    return (
      <LinkItem key={`link-${link.order}`}>
        <div className="link-input mt-30 mb-20">
          <LinksDropdown
            link={link}
            handleChange={handleChange}
            isLoading={isLoading}
            handleRemove={handleRemove}
            setOnKeyEnter={setOnKeyEnter}
            className="mb-5"
          />
          {link.type !== 'header' && (
            <InputField
              name="title"
              label="Title"
              value={link.title}
              autoComplete="off"
              onChange={(value) => handleChange(link.order, 'title', value)}
            />
          )}
        </div>
        {withHandle && <SortableHandleButton />}
      </LinkItem>
    );
  }
);

const SortableLinks = SortableContainer(
  ({
    links,
    handleChange,
    handleRemove,
    loaderData,
    setOnKeyEnter,
  }: {
    links: LinkWithTitle[];
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageLink>(
      order: number,
      field: T,
      value: TracPageLink[T]
    ) => void;
    loaderData: { isLoading: boolean; lastIdUpdated?: number };
    setOnKeyEnter: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    return (
      <div>
        {links.map((link, i) => (
          <SortableLinkItem
            link={link}
            index={i}
            key={i}
            withHandle={links.length > 1}
            handleChange={handleChange}
            handleRemove={handleRemove}
            isLoading={
              loaderData.isLoading &&
              loaderData.lastIdUpdated !== undefined &&
              link.order === loaderData.lastIdUpdated
            }
            setOnKeyEnter={setOnKeyEnter}
          />
        ))}
      </div>
    );
  }
);

const getLinkFromArray = (links: TracPageLink[], order: number) =>
  links.find((link) => link.order === order);

const getNewLinks = (
  links: LinkWithOldVer[],
  order: number,
  newData: Partial<LinkWithTitle>
) =>
  links.map((link) => {
    if (link.order === order) {
      return {
        ...(!link.oldLink ? { oldLink: link } : null),
        ...link,
        ...newData,
      };
    }
    return link;
  });

const LinksCtrl: React.FC<LinkCtrlProps> = ({
  links,
  className,
  updateLinks,
  closeMenu,
}) => {
  const [lastIdUpdated, setLastIdUpdated] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [onKeyEnter, setOnKeyEnter] = useState<boolean>(false);
  useEffect(() => {
    if (lastIdUpdated !== undefined) {
      const handler = setTimeout(() => {
        const updated = getLinkFromArray(links, lastIdUpdated);
        if (updated && updated.url && isValidUrl(updated.url) && onKeyEnter) {
          setIsLoading(true);
          parseLink(updated.url)
            .then((resp) => {
              const updatedLinks = getNewLinks(links, updated.order, {
                ...resp,
                showTitle: true,
              });
              updateLinks(updatedLinks);
              setLastIdUpdated(undefined);
              setIsLoading(false);
              setOnKeyEnter(false);
            })
            .catch(() => {
              setIsLoading(false);
              setOnKeyEnter(false);
            });
        }
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [lastIdUpdated, links, updateLinks, onKeyEnter]);

  const handleChange = <T extends keyof TracPageLink>(
    i: number,
    key: T,
    value: TracPageLink[T]
  ) => {
    const updatedLinks = getNewLinks(links, i, { [key]: value });
    updateLinks(updatedLinks);

    if (key === 'url') {
      const updated = getLinkFromArray(links, i);
      if (updated) {
        setLastIdUpdated(updated.order);
      }
    }
  };
  const handleAddMore = (type: 'url' | 'header') => {
    const newLink: TracPageLink[] = [
      {
        title: '',
        url: '',
        order: 0,
        type: type,
        schedule: false,
        highlight: false,
        start: '',
        end: '',
      },
    ];
    updateLinks(
      newLink.concat(
        links.map((link, i) => ({
          ...link,
          order: (link.order || i) + 1,
        }))
      )
    );
  };

  const getNewOrder = (items: TracPageLink[]) => {
    return items.map((item, order) => {
      return {
        ...item,
        order,
      };
    });
  };

  const handleRemove = (i: number) => {
    const newLinks = links.filter((link) => link.order !== i);
    const ordered = getNewOrder(newLinks);
    updateLinks(ordered);
  };

  const updateLinksOrder = ({ oldIndex, newIndex }: SortEnd) => {
    const newLinks = arrayMove(links, oldIndex, newIndex);
    const ordered = getNewOrder(newLinks);
    updateLinks(ordered);
  };
  const onSelect = (value: 'url' | 'header') => {
    handleAddMore(value);
    closeMenu();
  };
  return (
    <div className={className}>
      <Menu
        className={className}
        hasPaddingTop="padding-top:50px"
        isAddLinkTracpage
        button={
          <BoxHeader
            title="Links & Headers"
            text={`Add your content to your tracPage.`}
            hasButton
            noMargin
            buttonClose={false}
          />
        }
        menuId="ADD_LINK"
      >
        <LinkMenuItem>
          <Typography onClick={() => onSelect('url')}>Add new link</Typography>
          <Typography onClick={() => onSelect('header')}>Add header</Typography>
        </LinkMenuItem>
      </Menu>
      <SortableLinks
        links={links}
        handleChange={handleChange}
        handleRemove={handleRemove}
        lockAxis="y"
        useDragHandle
        helperClass="sorting"
        lockToContainerEdges={true}
        loaderData={{ isLoading, lastIdUpdated }}
        onSortEnd={(newSort) => updateLinksOrder(newSort)}
        setOnKeyEnter={setOnKeyEnter}
      />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LinksCtrl);

const LinkItem = styled.div`
  position: relative;

  > span {
    opacity: 0;
  }

  &:hover > span {
    opacity: 1;
  }
`;
const LinkMenuItem = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 30px;
  padding-right: 80px;
  border-radius: 0px;
  &:hover {
    opacity: 1;
  }
  z-index: 10000;
  span {
    display: block;
    font-weight: 700;
    padding-bottom: 5px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary};
  }
`;
